import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  connect() {
    // Skip if already initialized
    if (this.element.tomselect) {
      return;
    }

    // Dynamically import TomSelect to avoid SSR issues
    this.loadTomSelect();
  }

  async loadTomSelect() {
    try {
      const { default: TomSelect } = await import("tom-select");
      this.TomSelect = TomSelect;

      // Small delay to ensure DOM is ready (helps with Turbo frames)
      requestAnimationFrame(() => {
        this.initTomSelect();
      });
    } catch (error) {
      console.error("[SearchableSelect] Failed to load TomSelect:", error);
    }
  }

  // Phone country selects: either "US|1" style or digit-only calling codes (e.g. "233") as in live demo / phone_input partial.
  isPhoneCountryCodeSelect() {
    const sel = this.element;
    if (sel.dataset.searchableSelectPhoneCountry === "true") {
      return true;
    }
    const opts = [...sel.querySelectorAll("option[value]")].filter((o) => o.value.length > 0);
    if (opts.length === 0) {
      return false;
    }
    const first = opts[0].value;
    if (/^[A-Z]{2}\|\d+$/.test(first)) {
      return true;
    }
    if (opts.every((o) => /^\d+$/.test(o.value))) {
      return true;
    }
    return false;
  }

  initTomSelect() {
    if (!this.TomSelect) {
      return;
    }

    // Skip if already initialized (may have been initialized by another controller instance)
    if (this.element.tomselect) {
      return;
    }

    const selectElement = this.element;
    const isPhoneSelect = this.isPhoneCountryCodeSelect();

    try {
      const config = {
        maxOptions: null, // Show all options
        create: false,
        sortField: {
          field: "text",
          direction: "asc",
        },
        // Allow typing to search immediately
        openOnFocus: true,
        render: {
          option: function (data, escape) {
            // Show full text in dropdown for searching
            return `<div class="py-2 px-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">${escape(data.text)}</div>`;
          },
          no_results: function (_data, _escape) {
            return "<div class=\"py-2 px-3 text-gray-500\">No results found</div>";
          },
        },
        // Dispatch change event on the original select for other controllers to pick up
        onChange: (_value) => {
          selectElement.dispatchEvent(new Event("change", { bubbles: true }));
        },
      };

      // For phone country code selects, show shorter version in selected item
      if (isPhoneSelect) {
        config.placeholder = selectElement.dataset.searchableSelectSearchPlaceholder || "Search countries";
        config.render.item = function (data, escape) {
          // Extract flag emoji and phone code from text like "🇺🇸 United States +1".
          // Option text from the DOM often has leading newlines/indentation; trim that
          // before finding the first space (flag vs country name).
          const text = data.text;
          const lead = text.search(/\S/);
          const body = lead === -1 ? text.trim() : text.slice(lead).trimStart();
          const spaceIndex = body.indexOf(" ");
          const flag = spaceIndex > 0 ? body.substring(0, spaceIndex) : "";
          const codeMatch = body.match(/\+(\d+)\s*$/);
          if (flag && codeMatch) {
            return `<div class="flex items-center gap-1">${flag}<span class="tabular-nums">+${codeMatch[1]}</span></div>`;
          }
          return `<div class="flex items-center">${escape(data.text)}</div>`;
        };
      } else {
        // For regular selects, show full text
        config.render.item = function (data, escape) {
          return `<div class="flex items-center">${escape(data.text)}</div>`;
        };
      }

      this.tomSelect = new this.TomSelect(selectElement, config);

      // Apply custom styling after TomSelect is fully rendered
      // Use setTimeout to ensure the input element exists
      setTimeout(() => {
        this.applyCustomStyles();
      }, 50);
    } catch (error) {
      console.error("[SearchableSelect] Failed to initialize TomSelect:", error);
    }
  }

  applyCustomStyles() {
    const wrapper = this.element.parentElement?.querySelector(".ts-wrapper");
    if (wrapper) {
      // Add custom class for CSS targeting
      wrapper.classList.add("searchable-select-wrapper");

      // TomSelect copies classes from the original <select> onto the wrapper. Padding on
      // both wrapper and .ts-control doubles vertical space vs a native <input>.
      for (const cls of [...wrapper.classList]) {
        if (/^(p|px|py|pt|pb|pl|pr)-/.test(cls)) {
          wrapper.classList.remove(cls);
        }
      }

      // Remove any Tailwind background/styling classes that conflict with our CSS rules.
      wrapper.classList.remove(
        "bg-white", "dark:bg-gray-800", "bg-gray-800",
        "text-sm", "text-base", "text-gray-900", "text-gray-950", "dark:text-white",
        "focus:outline-none", "focus:ring-1", "focus:ring-2", "focus:ring-[#00ff88]", "focus:border-[#00ff88]",
        "focus:border-emerald-500/60", "focus:ring-emerald-500/25",
        "block"
      );

      // Tom Select copies the original select's component class onto its wrapper.
      // The phone component styles its wrapper separately, so retaining this class
      // applies the field padding twice and breaks the joined control height.
      if (wrapper.closest(".phone-input-country")) {
        wrapper.classList.remove("ui-phone-country");
      }

      // Text colors and dropdown styles are handled by CSS. Avoid inline
      // style mutations so the component works under the app CSP.
    }
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy();
      this.tomSelect = null;
    }
  }
}
